import React from 'react';
import { Check } from 'lucide-react';
import { Plan } from '../../types/plan';
import { PlanService } from '../../services/PlanService';
import { CHECKOUT_MESSAGES } from '../../constants/checkout';
import { summarizePlanDescription, summarizePlanFeatures } from '../../utils/checkoutPlanSummary';

interface CheckoutPlanDetailsProps {
  plan: Plan;
  paymentMethod?: 'card' | 'pix';
  showPaymentMethod?: boolean;
  /**
   * Quantidade efetivamente comprada (apenas para planos com allowCustomQuantity).
   * Quando informada, sobrescreve plan.quantity na linha "Período" do resumo.
   */
  effectiveQuantity?: number;
}

/** Bloco ampliado com nome, descrição, benefícios e dados do plano. */
const CheckoutPlanDetails: React.FC<CheckoutPlanDetailsProps> = ({
  plan,
  paymentMethod = 'card',
  showPaymentMethod = false,
  effectiveQuantity,
}) => {
  const { planDetails } = CHECKOUT_MESSAGES;
  const descriptionSummary = plan.description?.trim()
    ? summarizePlanDescription(plan.description)
    : '';
  const { items: featureItems, hiddenCount } = summarizePlanFeatures(plan.features ?? []);
  const hasFeatures = featureItems.length > 0;

  return (
    <section
      className="max-w-full overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white p-4 sm:p-5"
      aria-labelledby="checkout-plan-heading"
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <h3 id="checkout-plan-heading" className="break-words text-lg font-bold leading-tight text-gray-900 sm:text-xl">
          {plan.name}
        </h3>
        {plan.isFeatured && (
          <span className="rounded-full bg-[#FFD700]/20 px-2.5 py-0.5 text-xs font-semibold text-[#5C528A]">
            {planDetails.featuredBadge}
          </span>
        )}
      </div>

      {descriptionSummary && (
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#5C528A] mb-1.5">
            {planDetails.descriptionLabel}
          </p>
          <p className="break-words text-sm leading-relaxed text-gray-700">{descriptionSummary}</p>
        </div>
      )}

      {hasFeatures && (
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#5C528A] mb-2">
            {planDetails.includesLabel}
          </p>
          <ul className="space-y-2">
            {featureItems.map((feature, index) => (
              <li key={`${feature}-${index}`} className="flex items-start gap-2.5 text-sm text-gray-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 stroke-[2.5] text-[#4A7C75]" aria-hidden />
                <span className="min-w-0 break-words">{feature}</span>
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && (
            <p className="mt-2 text-xs text-gray-500">{planDetails.moreFeatures(hiddenCount)}</p>
          )}
        </div>
      )}

      <div className="space-y-2 border-t border-gray-200 pt-4 text-sm">
        <div className="flex justify-between gap-3 text-gray-600">
          <span>{planDetails.periodLabel}</span>
          <span className="min-w-0 shrink text-right font-medium text-gray-900 break-words">
            {PlanService.formatPeriod(
              Number.isInteger(effectiveQuantity) && (effectiveQuantity as number) > 0
                ? (effectiveQuantity as number)
                : plan.quantity,
              plan.billingType,
              plan.isRecurring
            )}
          </span>
        </div>
        <div className="flex justify-between gap-3 text-gray-600">
          <span>{planDetails.typeLabel}</span>
          <span className="min-w-0 shrink text-right font-medium text-gray-900 break-words">
            {plan.isRecurring ? planDetails.typeRecurring : planDetails.typeOneTime}
          </span>
        </div>
        {plan.sessionLabel && (
          <div className="flex justify-between gap-3 text-gray-600">
            <span>{planDetails.sessionLabel}</span>
            <span className="min-w-0 shrink text-right font-medium text-gray-900 break-words">{plan.sessionLabel}</span>
          </div>
        )}
        {showPaymentMethod && (
          <div className="flex justify-between gap-3 text-gray-600">
            <span>{planDetails.paymentMethodLabel}</span>
            <span className="min-w-0 shrink text-right font-medium text-gray-900 break-words">
              {paymentMethod === 'card' ? planDetails.paymentCard : planDetails.paymentPix}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default CheckoutPlanDetails;
